import { useEffect, useState } from "react";

export const useServiceWorker = () => {
  const [swRegistration, setServiceWorkerRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [swState, setSwState] = useState<string>("initializing");

  const checkIfBrowserSupportsWebPush = async () => {
    console.log("🔍 Checking browser Web Push support...");

    if (!("serviceWorker" in navigator)) {
      console.log("❌ Service Workers not supported");
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    const hasSupport = !!registration?.pushManager;
    console.log(`✓ Web Push support: ${hasSupport}`);
    return hasSupport;
  };

  const logServiceWorkerState = (registration: ServiceWorkerRegistration) => {
    const sw =
      registration.installing || registration.waiting || registration.active;
    if (sw) {
      console.log(`📡 Service Worker state: ${sw.state}`);
      sw.addEventListener("statechange", (e) => {
        const newState = (e.target as ServiceWorker).state;
        console.log(`📡 Service Worker state changed to: ${newState}`);
        setSwState(newState);
      });
    }
  };

  useEffect(() => {
    let mounted = true;

    const registerServiceWorker = async () => {
      try {
        console.log("🚀 Starting Service Worker registration process...");

        if (!("serviceWorker" in navigator)) {
          console.error("❌ Service Workers not supported in this browser");
          return;
        }

        const existingRegistration =
          await navigator.serviceWorker.getRegistration();

        if (existingRegistration) {
          console.log("🔄 Found existing Service Worker registration", {
            scope: existingRegistration.scope,
            state: existingRegistration.active?.state,
          });

          if (mounted) {
            setServiceWorkerRegistration(existingRegistration);
            logServiceWorkerState(existingRegistration);
          }
        } else {
          console.log(
            "📥 No existing Service Worker found, registering new one...",
          );

          const colorMode = window.matchMedia("(prefers-color-scheme: dark)")
            .matches
            ? "dark"
            : "light";

          const registration = await navigator.serviceWorker.register(
            `/sw.js?colorMode=${colorMode}`,
            {
              scope: "/",
            },
          );

          console.log("✅ Service Worker registered successfully", {
            scope: registration.scope,
          });

          if (mounted) {
            setServiceWorkerRegistration(registration);
            logServiceWorkerState(registration);
          }

          const browserSupportsWebPush = await checkIfBrowserSupportsWebPush();
          console.log("📱 Web Push capability check completed", {
            browserSupportsWebPush,
          });
        }

        // Wait for the service worker to be ready
        console.log("⏳ Waiting for Service Worker to be ready...");
        const readyRegistration = await navigator.serviceWorker.ready;
        console.log("✅ Service Worker is ready");

        if (mounted) {
          setServiceWorkerRegistration(readyRegistration);
          logServiceWorkerState(readyRegistration);
        }

        // Listen for updates
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          async () => {
            console.log("🔄 Service Worker controller changed");
            const newRegistration = await navigator.serviceWorker.ready;

            if (mounted) {
              setServiceWorkerRegistration(newRegistration);
              logServiceWorkerState(newRegistration);
            }
          },
        );

        // Listen for messages from the Service Worker
        navigator.serviceWorker.addEventListener("message", (event) => {
          console.log("📨 Received message from Service Worker:", event.data);
        });
      } catch (error) {
        console.error("❌ Service Worker registration failed:", error);
        setSwState("failed");
      }
    };

    registerServiceWorker();

    return () => {
      mounted = false;
      console.log("🧹 Cleaning up Service Worker hook");
    };
  }, []);

  const unregister = async () => {
    try {
      console.log("🔄 Starting Service Worker unregistration...");
      if (swRegistration) {
        await swRegistration.unregister();
        console.log("✅ Service Worker unregistered successfully");
        setServiceWorkerRegistration(null);
        setSwState("unregistered");
      }
    } catch (error) {
      console.error("❌ Service Worker unregistration failed:", error);
    }
  };

  return {
    swRegistration,
    swState,
    unregister,
    checkIfBrowserSupportsWebPush,
  };
};
